/**
 * Format detection helpers for the BOM upload flow.
 *
 * Templates themselves live in the per-company Firestore subcollection
 * `v3-companies/{companyId}/bom-format-templates`, accessed via the
 * useBomTemplates() hook.
 */

/**
 * Cheap, deterministic format detection from the filename. Misses real-world
 * cases where the customer's files have numeric SKU names (e.g. 1066-68.xlsx)
 * — prefer detectFormatFromWorkbook when the file has already been parsed;
 * this stays as a fallback.
 */
export const detectFormatKey = (fileName) => {
  if (/SM-|TM\d|Evolucio/i.test(fileName)) {
    return "pf-concept";
  }
  if (/\.xls$/i.test(fileName)) {
    return "legacy-xls";
  }
  return "storm-creek";
};

/**
 * Sheet-structure detection — the right signal once a file is parsed. PF Concept
 * workbooks always carry a "Workfile" + "Material table" pair; Storm Creek
 * exports always lead with a `Composition` + `Total Mass` header row. Falls back
 * to filename when neither signal is present.
 */
export const detectFormatFromWorkbook = (workbook, fileName) => {
  const sheets = workbook?.sheets || [];
  const sheetNames = sheets.map(({ name }) => String(name || "").toLowerCase());

  const hasWorkfile = sheetNames.some((name) => /workfile/.test(name));
  const hasMaterialTable = sheetNames.some((name) => /material\s*table/.test(name));
  if (hasWorkfile && hasMaterialTable) {
    return "pf-concept";
  }

  const firstSheetHeader = (sheets[0]?.rows?.[0] || [])
    .map((cell) => String(cell).toLowerCase())
    .join(" ");
  if (
    firstSheetHeader.includes("composition") &&
    firstSheetHeader.includes("total mass")
  ) {
    return "storm-creek";
  }

  return detectFormatKey(fileName);
};

export const findTemplate = (formatKey, templates) =>
  (templates || []).find((t) => t.formatKey === formatKey) || null;
